import { type App, appEnvUrlConfigs, type LocalApp, localApps } from 'ApplicationContext';

export interface CognitoPoolSettings {
  region: string;
  userPoolId: string;
  clientAppId: string;
}

const cognitoDevPool = {
  region: 'eu-central-1',
  userPoolId: 'eu-central-1_X1SQjEdlb',
};

const cognitoProdPool = {
  region: 'us-west-2',
  userPoolId: 'us-west-2_Lv9rFzXwh',
};

export const termlyId = '4c713548-2d73-4b47-8620-3f370f9bf49b';

export interface OAuthSettings {
  domain: string;
  autoSignIn: string;
  redirectSignIn: string;
  redirectSignOut: string;
}

export type BaseEnvType = 'dev' | 'prod';
export type EnvType = 'localhost-dev' | 'localhost-prod' | BaseEnvType;

export interface Domain {
  app: string;
  api: string;
  auth: string;
}

export interface CookieSettings {
  sameSite: 'none' | 'lax' | 'strict';
  secure: boolean;
  domain: string;
}

interface EnvConfiguration {
  apolloDevTools: boolean;
  formDevTools: boolean;
  logFormErrors: boolean;
  authSettings: (app: LocalApp) => {
    cognitoPool: CognitoPoolSettings;
    oAuthSettings: OAuthSettings;
  };
  apiHostnameAndPort: string;
  breakpointTools: boolean;
  clarityId?: string;
  showCookieConsent: boolean;
  showTestIds?: boolean;
  sentryEnvironment: string;
  sentryEnabled: boolean;
  logRequests: boolean;
  versionCheckInterval?: number;
  cookieDomain: string;
  externalAuthUrl: string;
  // New auth configuration aligned with other apps
  domain: Domain;
  cookieSettings: CookieSettings;
}

// Domain configurations aligned with other apps
export const localhostDomain: Domain = {
  app: 'localhost:8080', // Your UI app port
  api: 'api.genieai.fr', // Keep your existing API
  auth: 'localhost:3000', // Your external auth service
};

export const devDomain: Domain = {
  app: 'pms.dev.genieai.tech', // Assuming this is your app domain
  api: 'api.genieai.fr',
  auth: 'auth.genieai.fr',
};

export const prodDomain: Domain = {
  app: 'pms.genieai.tech', // Assuming this is your app domain
  api: 'api.genieai.tech',
  auth: 'auth.genieai.tech',
};

const localhost = {
  apolloDevTools: true,
  formDevTools: true,
  breakpointTools: true,
  logFormErrors: false,
  apiHostnameAndPort: '',
  showCookieConsent: false,
  showTestIds: true,
  sentryEnvironment: 'localhost',
  // for testing switch domain to "test.genie" and add it to hosts
  sentryEnabled: false,
  logRequests: false,
  versionCheckInterval: 60 * 1000 * 60,
  externalAuthUrl: `http://${localhostDomain.auth}`,
  domain: localhostDomain,
  cookieSettings: {
    sameSite: 'none' as const,
    secure: true,
    domain: 'localhost',
  },
} as const satisfies Partial<EnvConfiguration>;

export const getRedirectUrls = (envType: EnvType, application: App) => {
  if (envType.includes('localhost') && localApps.includes(application)) {
    return {
      redirectSignIn: 'http://localhost:8080',
      redirectSignOut: 'http://localhost:8080',
      autoSignIn: 'http://localhost:8080/signin',
    };
  }

  const baseEnvType = convertEnvTypeToBaseEnvType(envType);
  const settings = appEnvUrlConfigs[baseEnvType][application];

  return {
    redirectSignIn: settings.redirectSignInUrl,
    redirectSignOut: `https://${settings.domain}`,
    autoSignIn: `https://${settings.domain}${settings.autoSignInPath}`,
  };
};

const pmsDevClientId = '5ls4f04odcmmv0ncqlkdc1349q';
const pmsProdClientId = '6a9lp5h12fn9bba84he3reem9f';

const getAppClientId = (envType: BaseEnvType) => {
  return envType === 'dev' ? pmsDevClientId : pmsProdClientId;
};

const envConfig: Record<EnvType, EnvConfiguration> = {
  prod: {
    apolloDevTools: false,
    formDevTools: false,
    logFormErrors: false,
    breakpointTools: false,
    authSettings: (app: LocalApp) => ({
      cognitoPool: {
        ...cognitoProdPool,
        clientAppId: getAppClientId('prod'),
      },
      oAuthSettings: {
        domain: 'login.genieai.tech',
        ...getRedirectUrls('prod', app),
      },
    }),
    apiHostnameAndPort: `https://${prodDomain.api}`,
    clarityId: 'hbh06yv4c4',
    showCookieConsent: true,
    sentryEnvironment: 'production',
    sentryEnabled: true,
    logRequests: false,
    cookieDomain: '.genieai.tech',
    externalAuthUrl: `https://${prodDomain.auth}`,
    domain: prodDomain,
    cookieSettings: {
      sameSite: 'lax' as const,
      secure: true,
      domain: '.genieai.tech',
    },
  },
  dev: {
    apolloDevTools: true,
    formDevTools: false,
    breakpointTools: false,
    logFormErrors: false,
    authSettings: (app: LocalApp) => ({
      cognitoPool: {
        ...cognitoDevPool,
        clientAppId: getAppClientId('dev'),
      },
      oAuthSettings: {
        domain: 'login.dev.genieai.tech',
        ...getRedirectUrls('dev', app),
      },
    }),
    apiHostnameAndPort: `https://${devDomain.api}`,
    clarityId: 'hbgzzpvbxd',
    showCookieConsent: true,
    sentryEnvironment: 'development',
    sentryEnabled: true,
    logRequests: false,
    cookieDomain: '.genieai.fr',
    externalAuthUrl: `https://${devDomain.auth}`,
    domain: devDomain,
    cookieSettings: {
      sameSite: 'lax' as const,
      secure: true,
      domain: '.genieai.fr',
    },
  },
  'localhost-dev': {
    ...localhost,
    authSettings: (app: LocalApp) => ({
      cognitoPool: {
        ...cognitoDevPool,
        clientAppId: getAppClientId('dev'),
      },
      oAuthSettings: {
        domain: 'login.dev.genieai.tech',
        ...getRedirectUrls('localhost-dev', app),
      },
    }),
    cookieDomain: 'localhost',
  },
  'localhost-prod': {
    ...localhost,
    authSettings: (app) => ({
      cognitoPool: {
        ...cognitoProdPool,
        clientAppId: getAppClientId('prod'),
      },
      oAuthSettings: {
        domain: 'login.genieai.tech',
        ...getRedirectUrls('localhost-prod', app),
      },
    }),
    cookieDomain: 'localhost',
    domain: localhostDomain,
    cookieSettings: {
      sameSite: 'none' as const,
      secure: true,
      domain: 'localhost',
    },
  },
};

export const getEnvType = (): EnvType => {
  const mode = import.meta.env.MODE;
  if (mode === 'test') {
    return 'localhost-dev';
  }

  switch (window.location.hostname) {
    case appEnvUrlConfigs.prod.pms.domain:
    case appEnvUrlConfigs.prod.portfolioLab.domain:
      return 'prod';
    case appEnvUrlConfigs.dev.pms.domain:
    case appEnvUrlConfigs.dev.portfolioLab.domain:
      return 'dev';
    default: {
      if (import.meta.env.VITE_LOCALHOST_ENVIRONMENT === 'prod') {
        return 'localhost-prod';
      }
      return 'localhost-dev';
    }
  }
};

const convertEnvTypeToBaseEnvType = (envType: EnvType): BaseEnvType => {
  if (envType.includes('prod')) {
    return 'prod';
  }
  return 'dev';
};

export const getBaseEnvType = (): BaseEnvType => {
  const envType = getEnvType();
  return convertEnvTypeToBaseEnvType(envType);
};

export const getEnvConfig = (): EnvConfiguration => {
  return envConfig[getEnvType()];
};

export const oAuthResponseType = 'code';
export const oAuthScope = ['email', 'openid', 'aws.cognito.signin.user.admin'];
export const sentryDsn = 'https://c1e2cca49a350986af384ba5a648ddc7@o4506003148439552.ingest.sentry.io/4506383624306688';
